import { z } from 'zod';

import type { RouteFE } from '../route';
import { hubFromRestV1 } from './hubFromRestV1';
import { makeSanitizeWire, mapListPerEntry, safeOptional, toDate } from './restMapping';

const label = (field: string) => `routeFromRestV1.${field}`;

/** `createdAt` / `updatedAt` are camelCase here and on the nested hubs. */
const ROUTE_DATE_KEYS = new Set(['createdAt', 'updatedAt']);

const sanitizeRouteWire = makeSanitizeWire(ROUTE_DATE_KEYS);

const WireRouteObjectSchema = z.object({
    id: z.string().min(1),
    launchHub: safeOptional(z.unknown(), label('launchHub')),
    landingHub: safeOptional(z.unknown(), label('landingHub')),
    launchHubId: safeOptional(z.string(), label('launchHubId')),
    landingHubId: safeOptional(z.string(), label('landingHubId')),
    distance: safeOptional(z.number(), label('distance')),
    averageDuration: safeOptional(z.number(), label('averageDuration')),
    waypoints: safeOptional(z.array(z.unknown()), label('waypoints')),
    createdAt: safeOptional(z.string(), label('createdAt')),
    updatedAt: safeOptional(z.string(), label('updatedAt')),
});

/** Exported so `mocks/tests/restContract.test.ts` can assert a mock body against the mapper's validator. */
export const WireRouteSchema = z.preprocess(sanitizeRouteWire, WireRouteObjectSchema);

type WireRouteObject = z.infer<typeof WireRouteObjectSchema>;

export interface PredefinedRouteWireSpec {
    id: string;
    launchHub?: unknown;
    landingHub?: unknown;
    createdAt: string;
    updatedAt: string;
    distance: number;
    averageDuration: number;
    waypoints: unknown[];
    launchHubId?: string;
    landingHubId?: string;
}

type KeysExistOn<TWire, TSpec> = Exclude<keyof TWire, keyof TSpec> extends never ? true : never;

export type RouteWireKeysExistOnSpec = {
    route: KeysExistOn<WireRouteObject, PredefinedRouteWireSpec>;
};

/** Maps one `PredefinedRouteTO` body onto the domain {@link RouteFE}. Throws only on a missing id. */
export const routeFromRestV1 = (raw: unknown): RouteFE => {
    const wire = WireRouteSchema.parse(raw);
    return {
        id: wire.id,
        launchHub: wire.launchHub == null ? undefined : hubFromRestV1(wire.launchHub),
        landingHub: wire.landingHub == null ? undefined : hubFromRestV1(wire.landingHub),
        distance: wire.distance ?? 0,
        averageDuration: wire.averageDuration ?? 0,
        createdAt: toDate(wire.createdAt) ?? null,
        updatedAt: toDate(wire.updatedAt) ?? null,
    };
};

/**
 * Maps a `GET /api/v1/routes/predefined` body. Each entry is mapped on its own, so a route whose
 * identity is unusable is dropped and logged while every other route still reaches the UI.
 */
export const routesFromRestV1 = (raw: unknown): RouteFE[] => mapListPerEntry(raw, routeFromRestV1, label('list'));
